import { Col, Row, Modal, NavLink } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  authSelector,
  organizationDocumentsSelector,
} from "../../reducers";
import { getAllOrganizationDocuments } from "../../actions/organization/organizationdocumentsgetall";
import { useEffect, useState, useMemo } from "react";
import { storageGet, organizationDocumentGet } from "../../api";

import {
  IconInstructionsAirlineSeat,
  IconInstructionsAirportShuttle,
  IconInstructionsApparel,
  IconInstructionsDictionary,
  IconInstructionsEncrypted,
  IconInstructionsHeartSmile,
  IconInstructionsFireTruck,
  IconInstructionsLockPerson,
  IconInstructionsMenstrualHealth,
  IconInstructionsPartnerHeart,
  IconInstructionsSupportAgent,
  IconInstructionsOther,
} from "../../common/icons";

export function PersonalInstructions() {
  const { t } = useTranslation();
  const auth = useSelector(authSelector);
  const dispatch = useDispatch();

  const organizationDocuments = useSelector(organizationDocumentsSelector);
  const [attachmentModal, setAttachmentModal] = useState(null);

  useEffect(() => {
    if (auth?.companyNumber && auth?.companyBusinessId) {
      dispatch(getAllOrganizationDocuments(auth.companyBusinessId));
    }
  }, [dispatch, auth?.companyNumber, auth?.companyBusinessId]);

  const instructionConfig = useMemo(
    () => [
      {
        category: "CBA",
        label: t("CBA", "Työehtosopimus"),
        icon: <IconInstructionsDictionary className="instruction-grid-icon" />,
        className: "instructions-blue",
      },
      {
        category: "OccupationalSafetyAndHealth",
        label: t("OccupationalSafetyAndHealth", "Työsuojelu"),
        icon: <IconInstructionsPartnerHeart className="instruction-grid-icon" />,
        className: "instructions-blue",
      },
      {
        category: "OccupationalHealth",
        label: t("OccupationalHealth", "Työterveys"),
        icon: <IconInstructionsMenstrualHealth className="instruction-grid-icon" />,
        className: "instructions-green",
      },
      {
        category: "TravelGuidelines",
        label: t("TravelGuidelines", "Matkustusohjeet"),
        icon: <IconInstructionsAirportShuttle className="instruction-grid-icon" />,
        className: "instructions-green",
      },
      {
        category: "OccupationalSafety",
        label: t("OccupationalSafety", "Työturvallisuus"),
        icon: <IconInstructionsEncrypted className="instruction-grid-icon" />,
        className: "instructions-dark-blue",
      },
      {
        category: "InformationSecurity",
        label: t("InformationSecurity", "Tietoturva"),
        icon: <IconInstructionsLockPerson className="instruction-grid-icon" />,
        className: "instructions-dark-blue",
      },
      {
        category: "PersonnelBenefits",
        label: t("PersonnelBenefits", "Työntekijäedut"),
        icon: <IconInstructionsHeartSmile className="instruction-grid-icon" />,
        className: "instructions-red",
      },
      {
        category: "FireSafety",
        label: t("FireSafety", "Paloturvallisuus"),
        icon: <IconInstructionsFireTruck className="instruction-grid-icon" />,
        className: "instructions-yellow",
      },
      {
        category: "HelpDesk",
        label: t("HelpDesk", "Help desk"),
        icon: <IconInstructionsSupportAgent className="instruction-grid-icon" />,
        className: "instructions-yellow",
      },
      {
        category: "DressCode",
        label: t("DressCode", "Pukuohjeistus"),
        icon: <IconInstructionsApparel className="instruction-grid-icon" />,
        className: "instructions-purple",
      },
      {
        category: "Ergonomy",
        label: t("Ergonomy", "Ergonomia"),
        icon: <IconInstructionsAirlineSeat className="instruction-grid-icon" />,
        className: "instructions-purple",
      },
      {
        category: "OtherInstruction",
        label: t("OtherInstruction", "Muu ohjeistus"),
        icon: <IconInstructionsOther className="instruction-grid-icon" />,
        className: "instructions-blue",
      },
    ],
    [t]
  );

  const ownProfileMap = useMemo(() => {
    const map = {};
    (organizationDocuments ?? []).forEach((doc) => {
      if (doc.documentLocation === "OWNPROFILE") {
        map[doc.documentCategory] = doc;
      }
    });
    return map;
  }, [organizationDocuments]);

  async function openDocumentByCategory(category) {
    const doc = ownProfileMap?.[category];

    if (!doc) {
      alert(t("NoDocumentFound", "Tämän kategorian dokumenttia ei löytynyt."));
      return;
    }

    const instructionDocument = await organizationDocumentGet(doc.documentId);
    const attachments = instructionDocument?.data?.attachments?.result || [];

    if (!attachments.length) {
      alert(t("NoAttachment", "Dokumentilla ei ole liitteitä."));
      return;
    }

    const attachment = attachments[0];
    const res = await storageGet({ key: attachment.key });

    if (res.status === 200) {
      const url = URL.createObjectURL(res.data);

      setAttachmentModal({
        objectURL: url,
        filename: attachment.filename || "document",
      });
    }
  }

  const hasAnyInstructions = instructionConfig.some(
    (c) => ownProfileMap?.[c.category]
  );

  return (
    <>
      <h4 style={{ color: "var(--heeros-blue)", marginBottom: "1rem" }}>
        {t("PersonalInstructions", "Ohjeistukset")}
      </h4>

      <Row className="g-3">
        {instructionConfig
          .filter((c) => ownProfileMap?.[c.category])
          .map((c) => (
            <Col key={c.category} xs={3}>
              <div
                className={`${c.className} instruction-grid-box d-flex flex-column align-items-center`}
                style={{ cursor: "pointer" }}
                onClick={() => openDocumentByCategory(c.category)}
              >
                {c.icon}
                <div className="instruction-grid-label">{c.label}</div>
              </div>
            </Col>
          ))}
      </Row>

      {!hasAnyInstructions && (
        <div
          style={{
            marginTop: "1rem",
            color: "#b0b0b0",
            fontStyle: "italic",
          }}
        >
          {t(
            "NoInstructionsInfo",
            "Organisaatiollasi ei ole lisättyjä dokumentteja"
          )}
        </div>
      )}

      {attachmentModal && (
        <Modal
          show
          onHide={() => {
            URL.revokeObjectURL(attachmentModal.objectURL);
            setAttachmentModal(null);
          }}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <NavLink
              href={attachmentModal.objectURL}
              target="_blank"
              rel="noreferrer"
              download={attachmentModal.filename}
            >
              ⭳ {attachmentModal.filename}
            </NavLink>
          </Modal.Header>

          <iframe
            style={{ height: "85vh", width: "100%" }}
            src={attachmentModal.objectURL}
          />
        </Modal>
      )}
    </>
  );
}